import { useState } from 'react';
import { MapPin, Phone, Mail, Instagram, Send, Clock, MessageCircle, Building2, CheckCircle } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import { companyInfo } from '../mock';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    condominiumName: '',
    condominiumAddress: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const subjectLabels = {
    'reforma-fachada': 'Reforma de Fachada',
    'manutencao': 'Manutenção Predial',
    'impermeabilizacao': 'Impermeabilização',
    'pintura': 'Pintura e Alvenaria',
    'hidraulica': 'Serviços Hidráulicos',
    'eletrica': 'Serviços Elétricos',
    'telhado': 'Manutenção de Telhados',
    'inspecao-drone': 'Inspeção com Drone',
    'orcamento': 'Solicitar Orçamento Geral',
    'outros': 'Outros Assuntos'
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubjectChange = (value) => {
    setFormData({
      ...formData,
      subject: value
    });
  };

  const formatPhoneNumber = (phone) => {
    // Remove tudo exceto números
    return phone.replace(/\D/g, '');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Formatar mensagem para WhatsApp
    const subjectLabel = subjectLabels[formData.subject] || formData.subject;
    
    const whatsappMessage = `🏢 *NOVA SOLICITAÇÃO DE ORÇAMENTO*
━━━━━━━━━━━━━━━━━━━━━━

👤 *Dados do Contato:*
• Nome: ${formData.name}
• Telefone: ${formData.phone}
• E-mail: ${formData.email}

🏗️ *Dados do Condomínio:*
• Nome: ${formData.condominiumName || 'Não informado'}
• Endereço: ${formData.condominiumAddress || 'Não informado'}

📋 *Assunto:* ${subjectLabel}

💬 *Mensagem:*
${formData.message}

━━━━━━━━━━━━━━━━━━━━━━
_Enviado pelo site LUVCORP_`;

    // Criar URL do WhatsApp
    const whatsappUrl = `https://wa.me/${companyInfo.whatsapp}?text=${encodeURIComponent(whatsappMessage)}`;
    
    // Mostrar toast de sucesso
    toast.success('Redirecionando para o WhatsApp!', {
      description: 'Complete o envio da mensagem no WhatsApp.',
      duration: 4000,
    });
    
    // Abrir WhatsApp em nova aba
    setTimeout(() => {
      window.open(whatsappUrl, '_blank');
      setIsSubmitting(false);
      
      // Reset form após envio
      setFormData({
        name: '',
        email: '',
        phone: '',
        condominiumName: '',
        condominiumAddress: '',
        subject: '',
        message: ''
      });
    }, 500);
  };

  return (
    <div className="mt-20">
      <Toaster position="top-right" richColors />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-gray-900 text-white py-24">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge className="bg-white/20 text-white hover:bg-white/30">
              Entre em Contato
            </Badge>
            <h1 className="text-5xl font-bold">
              Vamos Transformar Seu Projeto em Realidade
            </h1>
            <p className="text-xl text-blue-100">
              Entre em contato conosco para solicitar um orçamento gratuito ou tirar suas dúvidas. Nossa equipe está pronta para atender você.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Informações de Contato</h2>
                <p className="text-gray-600 leading-relaxed">
                  Estamos à disposição para atendê-lo. Entre em contato através de qualquer um dos canais abaixo ou preencha o formulário ao lado.
                </p>
              </div>

              <div className="space-y-6">
                {/* Address */}
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#15448A]/10 flex items-center justify-center">
                      <MapPin className="text-[#15448A]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Endereço</h3>
                      <p className="text-gray-600">{companyInfo.address}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Phone */}
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                      <Phone className="text-green-600" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Telefone / WhatsApp</h3>
                      <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="text-gray-600 hover:text-[#15448A] transition-colors">
                        {companyInfo.phone}
                      </a>
                    </div>
                  </CardContent>
                </Card>

                {/* Email */}
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                      <Mail className="text-[#F47A20]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">E-mail</h3>
                      <a href={`mailto:${companyInfo.email}`} className="text-gray-600 hover:text-[#15448A] transition-colors break-all">
                        {companyInfo.email}
                      </a>
                    </div>
                  </CardContent>
                </Card>

                {/* Instagram */}
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-pink-100 flex items-center justify-center">
                      <Instagram className="text-pink-600" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Instagram</h3>
                      <a href={companyInfo.instagramUrl} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#15448A] transition-colors">
                        {companyInfo.instagram}
                      </a>
                    </div>
                  </CardContent>
                </Card>

                {/* Business Hours */}
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                      <Clock className="text-purple-600" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Horário de Atendimento</h3>
                      <p className="text-gray-600">Segunda a Sexta: 8h - 18h</p>
                      <p className="text-gray-600">Sábado: 8h - 13h</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="shadow-xl border-2 border-gray-100">
                <CardContent className="p-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                      <MessageCircle className="text-white" size={24} />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Solicitar Orçamento</h2>
                      <p className="text-sm text-gray-500">Resposta rápida via WhatsApp</p>
                    </div>
                  </div>
                  
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Dados Pessoais */}
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                        <span className="w-6 h-6 rounded-full bg-[#15448A] text-white flex items-center justify-center text-xs">1</span>
                        <span>Seus Dados</span>
                      </div>
                      
                      <div>
                        <Label htmlFor="name">Nome Completo *</Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Seu nome completo"
                          className="mt-1.5"
                          data-testid="contact-name-input"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="email">E-mail *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="seu@email.com"
                            className="mt-1.5"
                            data-testid="contact-email-input"
                          />
                        </div>

                        <div>
                          <Label htmlFor="phone">WhatsApp *</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            required
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="(21) 98765-4321"
                            className="mt-1.5"
                            data-testid="contact-phone-input"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Dados do Condomínio */}
                    <div className="space-y-4 pt-2">
                      <div className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                        <span className="w-6 h-6 rounded-full bg-[#15448A] text-white flex items-center justify-center text-xs">2</span>
                        <span>Dados do Condomínio</span>
                      </div>
                      
                      <div>
                        <Label htmlFor="condominiumName">Nome do Condomínio</Label>
                        <Input
                          id="condominiumName"
                          name="condominiumName"
                          type="text"
                          value={formData.condominiumName}
                          onChange={handleChange}
                          placeholder="Ex: Condomínio Residencial Sol"
                          className="mt-1.5"
                          data-testid="contact-condominium-name-input"
                        />
                      </div>

                      <div>
                        <Label htmlFor="condominiumAddress">Endereço do Condomínio</Label>
                        <Input
                          id="condominiumAddress"
                          name="condominiumAddress"
                          type="text"
                          value={formData.condominiumAddress}
                          onChange={handleChange}
                          placeholder="Rua, número e bairro"
                          className="mt-1.5"
                          data-testid="contact-condominium-address-input"
                        />
                      </div>
                    </div>

                    {/* Assunto e Mensagem */}
                    <div className="space-y-4 pt-2">
                      <div className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                        <span className="w-6 h-6 rounded-full bg-[#15448A] text-white flex items-center justify-center text-xs">3</span>
                        <span>Detalhes do Serviço</span>
                      </div>

                      <div>
                        <Label htmlFor="subject">Tipo de Serviço *</Label>
                        <Select value={formData.subject} onValueChange={handleSubjectChange} required>
                          <SelectTrigger className="mt-1.5" data-testid="contact-subject-select">
                            <SelectValue placeholder="Selecione o serviço desejado" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="reforma-fachada">Reforma de Fachada</SelectItem>
                            <SelectItem value="impermeabilizacao">Impermeabilização</SelectItem>
                            <SelectItem value="pintura">Pintura e Alvenaria</SelectItem>
                            <SelectItem value="hidraulica">Serviços Hidráulicos</SelectItem>
                            <SelectItem value="eletrica">Serviços Elétricos</SelectItem>
                            <SelectItem value="telhado">Manutenção de Telhados</SelectItem>
                            <SelectItem value="inspecao-drone">Inspeção com Drone 4K</SelectItem>
                            <SelectItem value="manutencao">Manutenção Predial Geral</SelectItem>
                            <SelectItem value="orcamento">Orçamento Geral</SelectItem>
                            <SelectItem value="outros">Outros Assuntos</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="message">Descreva sua Necessidade *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          required
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Conte-nos sobre o problema ou serviço que precisa. Quanto mais detalhes, melhor poderemos ajudar..."
                          rows={4}
                          className="mt-1.5"
                          data-testid="contact-message-textarea"
                        />
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg text-lg py-6"
                      data-testid="contact-submit-button"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Abrindo WhatsApp...
                        </>
                      ) : (
                        <>
                          <MessageCircle className="mr-2" size={20} />
                          Enviar pelo WhatsApp
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-center text-gray-500">
                      Ao clicar, você será redirecionado para o WhatsApp com sua mensagem pré-preenchida.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp CTA */}
      <section className="section-padding bg-gradient-to-r from-green-500 to-green-600 text-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="inline-block p-4 bg-white/20 rounded-full mb-4">
              <MessageCircle size={40} />
            </div>
            <h2 className="text-4xl font-bold">
              Prefere Falar Diretamente?
            </h2>
            <p className="text-xl text-green-50">
              Clique no botão abaixo e converse agora mesmo com nossa equipe pelo WhatsApp!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`https://wa.me/${companyInfo.whatsapp}?text=${encodeURIComponent('Olá! Vim pelo site e gostaria de solicitar um orçamento.')}`} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-white text-green-600 hover:bg-gray-100 shadow-xl text-lg px-8 py-6">
                  <MessageCircle className="mr-2" size={20} />
                  Abrir WhatsApp
                </Button>
              </a>
              <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}>
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6">
                  <Phone className="mr-2" size={20} />
                  Ligar Agora
                </Button>
              </a>
            </div>
            <p className="text-sm text-green-100">
              Atendimento: Segunda a Sexta, 8h às 18h | Sábado, 8h às 13h
            </p>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 bg-gray-50">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center items-center gap-8 text-gray-500">
            <div className="flex items-center space-x-2">
              <CheckCircle size={20} className="text-green-500" />
              <span className="text-sm">Orçamento Gratuito</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle size={20} className="text-green-500" />
              <span className="text-sm">Resposta em até 24h</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle size={20} className="text-green-500" />
              <span className="text-sm">+45 Condomínios Atendidos</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle size={20} className="text-green-500" />
              <span className="text-sm">5+ Anos de Experiência</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;